import { useState } from 'react';
import { Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useScenarios, type Scenario } from 'hooks/scenarios/scenarioHooks';
import { describeScenario } from './scenarioFormModel';
import { LaunchScenarioDialog } from './LaunchScenarioDialog';

type Props = {
  /** Attaches the launch to an incident, if one is in context. */
  incidentId?: string | null;
  /** Attaches the launch to an org event, if one is in context. */
  orgEventId?: string | null;
  label?: string;
  size?: 'xs' | 'sm';
  variant?: 'default' | 'outline' | 'secondary' | 'ghost';
};

/**
 * Picks a saved response and launches it against whatever is in context.
 *
 * Renders nothing when the organization has no scenarios: an empty menu invites
 * a click that leads nowhere, and the place to author one is the scenarios
 * page, not an incident toolbar.
 */
export const LaunchScenarioButton: React.FC<Props> = ({
  incidentId,
  orgEventId,
  label = 'Launch Scenario',
  size = 'sm',
  variant = 'outline',
}) => {
  const { data: scenarios = [] } = useScenarios();
  const [launching, setLaunching] = useState<Scenario | null>(null);

  if (scenarios.length === 0) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              type="button"
              size={size}
              variant={variant}
              className="tw:gap-1"
            />
          }
        >
          <Play className="tw:size-3.5" />
          {label}
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="dark tw:max-w-[320px]">
          {scenarios.map((scenario) => (
            <DropdownMenuItem
              key={scenario.id}
              onClick={() => setLaunching(scenario)}
            >
              <span className="tw:flex tw:flex-col tw:gap-0.5">
                <span>{scenario.name}</span>
                <span className="tw:text-[10px] tw:text-muted-foreground">
                  {describeScenario(scenario)}
                </span>
              </span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <LaunchScenarioDialog
        scenario={launching}
        incidentId={incidentId}
        orgEventId={orgEventId}
        onClose={() => setLaunching(null)}
      />
    </>
  );
};

export default LaunchScenarioButton;
